"use client";

import type { Address, Order, OrderHistorySimple } from "@workspace/ui/types";
import type { Dispatch } from "react";
import React, { createContext, useContext, useMemo, useReducer } from "react";

import {
  AccountInfoAPI,
  AddressDetailsAPI,
  AllAddressesAPI,
  LoginAPI,
  LogoutAPI,
  OrderHistoryAPI,
  RegisterAPI,
  ResetPasswordAPI,
  UpdateCustomerAPI,
} from "@/const/endPoint";
import { camelToSnakeCaseObject, snakeToCamelCaseObject } from "@/utils";
import axiosInstance from "@/utils/api/fetchData/axiosInstance";

export enum CustomerActionType {
  // ACCOUNT INFO
  START_ACCOUNT_INFO = "START_ACCOUNT_INFO",
  FINISH_ACCOUNT_INFO = "FINISH_ACCOUNT_INFO",

  // SIGN IN
  START_SIGN_IN = "START_SIGN_IN",
  SUCCESS_SIGN_IN = "SUCCESS_SIGN_IN",
  FAILURE_SIGN_IN = "FAILURE_SIGN_IN",

  // SIGN UP
  START_SIGN_UP = "START_SIGN_UP",
  SUCCESS_SIGN_UP = "SUCCESS_SIGN_UP",
  FAILURE_SIGN_UP = "FAILURE_SIGN_UP",

  // RESET PASSWORD
  START_RESET_PASSWORD = "START_RESET_PASSWORD",
  SUCCESS_RESET_PASSWORD = "SUCCESS_RESET_PASSWORD",
  FAILURE_RESET_PASSWORD = "FAILURE_RESET_PASSWORD",

  // LOGOUT
  LOGOUT = "LOGOUT",

  // UPDATE CUSTOMER
  START_UPDATE_CUSTOMER = "START_UPDATE_CUSTOMER",
  SUCCESS_UPDATE_CUSTOMER = "SUCCESS_UPDATE_CUSTOMER",
  FAILURE_UPDATE_CUSTOMER = "FAILURE_UPDATE_CUSTOMER",

  // ORDER HISTORIES
  START_ORDER_HISTORIES = "START_ORDER_HISTORIES",
  SUCCESS_ORDER_HISTORIES = "SUCCESS_ORDER_HISTORIES",
  FAILURE_ORDER_HISTORIES = "FAILURE_ORDER_HISTORIES",

  // ORDER DETAILS
  START_ORDER_DETAILS = "START_ORDER_DETAILS",
  SUCCESS_ORDER_DETAILS = "SUCCESS_ORDER_DETAILS",
  FAILURE_ORDER_DETAILS = "FAILURE_ORDER_DETAILS",

  // ADDRESSES
  START_ADDRESSES = "START_ADDRESSES",
  SUCCESS_ADDRESSES = "SUCCESS_ADDRESSES",
  FAILURE_ADDRESSES = "FAILURE_ADDRESSES",

  // ADDRESS DETAILS
  START_ADDRESS_DETAILS = "START_ADDRESS_DETAILS",
  SUCCESS_ADDRESS_DETAILS = "SUCCESS_ADDRESS_DETAILS",
  FAILURE_ADDRESS_DETAILS = "FAILURE_ADDRESS_DETAILS",

  // CREATE ADDRESS
  START_CREATE_ADDRESS = "START_CREATE_ADDRESS",
  SUCCESS_CREATE_ADDRESS = "SUCCESS_CREATE_ADDRESS",
  FAILURE_CREATE_ADDRESS = "FAILURE_CREATE_ADDRESS",

  // UPDATE ADDRESS
  START_UPDATE_ADDRESS = "START_UPDATE_ADDRESS",
  SUCCESS_UPDATE_ADDRESS = "SUCCESS_UPDATE_ADDRESS",
  FAILURE_UPDATE_ADDRESS = "FAILURE_UPDATE_ADDRESS",

  // DELETE ADDRESS
  START_DELETE_ADDRESS = "START_DELETE_ADDRESS",
  SUCCESS_DELETE_ADDRESS = "SUCCESS_DELETE_ADDRESS",
  FAILURE_DELETE_ADDRESS = "FAILURE_DELETE_ADDRESS",
}

export type AccountFormValues = {
  email: string;
  firstName: string;
  lastName: string;
  idGender: 1 | 2;
  password: string;
  newPassword: string;
  newPasswordConfirmation: string;
  newsletter: boolean;
};

export type AddressFormValues = {
  alias: string;
  company: string;
  vatNumber: string;
  firstName: string;
  lastName: string;
  address1: string;
  address2: string;
  postcode: string;
  city: string;
  phone: string;
  country: string;
  idCountry: string;
  companyAddress?: boolean;
};

export type CustomerState = {
  isLoadingAccountInfo: boolean;

  customer: Customer | null;
  isLoggedIn: boolean;
  isLoading: boolean;
  error: string | null;

  orderHistories: OrderHistorySimple[] | null;
  isOrderHistoriesLoading: boolean;
  orderHistoriesError: string | null;

  orderDetails: {
    [key: number | string]: Order | null;
  } | null;
  isOrderDetailsLoading: {
    [key: number | string]: boolean;
  };
  orderDetailsError: string | null;

  addresses: Address[] | null;
  isAddressesLoading: boolean;
  addressesError: string | null;

  addressDetails: Address | null;
  isAddressDetailsLoading: boolean;
  addressDetailsError: string | null;

  isCreateAddressLoading: boolean;
  createAddressError: string | null;

  isUpdateAddressLoading: boolean;
  updateAddressError: string | null;

  isDeleteAddressLoading: boolean;
  deleteAddressError: string | null;

  isResetPasswordLoading: boolean;
  resetPasswordError: string | null;
};

export interface Customer {
  id: string;
  idLang: string;
  idShop: string;
  idShopList: any[];
  forceId: boolean;
  idShopGroup: string;
  note: string;
  idGender: string;
  idDefaultGroup: string;
  lastname: string;
  firstname: string;
  birthday: string;
  email: string;
  newsletter: string;
  ipRegistrationNewsletter: string;
  newsletterDateAdd: string;
  optin: string;
  website: string;
  company: string;
  siret: string;
  ape: string;
  outstandingAllowAmount: string;
  showPublicPrices: string;
  idRisk: string;
  maxPaymentDays: string;
  active: string;
  isGuest: string;
  deleted: string;
  dateAdd: string;
  dateUpd: string;
  years: number | null;
  days: number | null;
  months: number | null;
  geolocIdCountry: string | null;
  geolocIdState: string | null;
  geolocPostcode: string | null;
  logged: boolean;
  idGuest: string | null;
  groupBox: any | null;
}

export interface SignInRequestProps {
  email: string;
  password: string;
}

export interface SignUpRequestProps {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  gender: 1 | 2;
}

const CustomerStateContext = createContext<CustomerState | undefined>(
  undefined,
);

type CustomerAction =
  | { type: CustomerActionType.START_ACCOUNT_INFO }
  | { type: CustomerActionType.FINISH_ACCOUNT_INFO }

  // Sign in
  | { type: CustomerActionType.START_SIGN_IN }
  | {
      type: CustomerActionType.SUCCESS_SIGN_IN;
      customer: Customer;
    }
  | { type: CustomerActionType.FAILURE_SIGN_IN; error: string }

  // Sign up
  | { type: CustomerActionType.START_SIGN_UP }
  | { type: CustomerActionType.SUCCESS_SIGN_UP }
  | { type: CustomerActionType.FAILURE_SIGN_UP; error: string }

  // Reset password
  | { type: CustomerActionType.START_RESET_PASSWORD }
  | { type: CustomerActionType.SUCCESS_RESET_PASSWORD }
  | { type: CustomerActionType.FAILURE_RESET_PASSWORD; error: string }

  // Logout
  | { type: CustomerActionType.LOGOUT }
  | { type: CustomerActionType.START_UPDATE_CUSTOMER }
  | { type: CustomerActionType.SUCCESS_UPDATE_CUSTOMER }
  | { type: CustomerActionType.FAILURE_UPDATE_CUSTOMER; error: string }
  // ORDER HISTORY
  | { type: CustomerActionType.START_ORDER_HISTORIES }
  | {
      type: CustomerActionType.SUCCESS_ORDER_HISTORIES;
      orderHistories: OrderHistorySimple[];
    }
  | { type: CustomerActionType.FAILURE_ORDER_HISTORIES; error: string }
  // ORDER DETAILS
  | {
      type: CustomerActionType.START_ORDER_DETAILS;
      isOrderDetailsLoading: {
        [key: number | string]: boolean;
      };
    }
  | {
      type: CustomerActionType.SUCCESS_ORDER_DETAILS;
      isOrderDetailsLoading: {
        [key: number | string]: boolean;
      };
      orderDetails: {
        [key: number | string]: Order | null;
      };
    }
  | {
      type: CustomerActionType.FAILURE_ORDER_DETAILS;
      isOrderDetailsLoading: {
        [key: number | string]: boolean;
      };
      error: string;
    }
  // ADDRESSES
  | { type: CustomerActionType.START_ADDRESSES }
  | { type: CustomerActionType.SUCCESS_ADDRESSES; addresses: Address[] }
  | { type: CustomerActionType.FAILURE_ADDRESSES; error: string }
  // ADDRESS DETAILS
  | { type: CustomerActionType.START_ADDRESS_DETAILS }
  | { type: CustomerActionType.SUCCESS_ADDRESS_DETAILS; address: Address }
  | { type: CustomerActionType.FAILURE_ADDRESS_DETAILS; error: string }
  // CREATE ADDRESS
  | { type: CustomerActionType.START_CREATE_ADDRESS }
  | { type: CustomerActionType.SUCCESS_CREATE_ADDRESS; address: Address }
  | { type: CustomerActionType.FAILURE_CREATE_ADDRESS; error: string }
  // UPDATE ADDRESS
  | { type: CustomerActionType.START_UPDATE_ADDRESS }
  | { type: CustomerActionType.SUCCESS_UPDATE_ADDRESS; address: Address }
  | { type: CustomerActionType.FAILURE_UPDATE_ADDRESS; error: string }
  // DELETE ADDRESS
  | { type: CustomerActionType.START_DELETE_ADDRESS }
  | { type: CustomerActionType.SUCCESS_DELETE_ADDRESS; idAddress: string }
  | { type: CustomerActionType.FAILURE_DELETE_ADDRESS; error: string };

export type CustomerDispatch = Dispatch<CustomerAction>;

const CustomerDispatchContext = createContext<CustomerDispatch | undefined>(
  undefined,
);

const initialCustomerState: CustomerState = {
  isLoadingAccountInfo: true,

  customer: null,
  isLoggedIn: false,
  isLoading: false,
  error: null,

  orderHistories: null,
  isOrderHistoriesLoading: false,
  orderHistoriesError: null,

  orderDetails: null,
  isOrderDetailsLoading: {},
  orderDetailsError: null,

  addresses: null,
  isAddressesLoading: false,
  addressesError: null,

  addressDetails: null,
  isAddressDetailsLoading: false,
  addressDetailsError: null,

  isCreateAddressLoading: false,
  createAddressError: null,

  isUpdateAddressLoading: false,
  updateAddressError: null,

  isDeleteAddressLoading: false,
  deleteAddressError: null,

  isResetPasswordLoading: false,
  resetPasswordError: null,
};

function customerReducer(
  state: CustomerState,
  action: CustomerAction,
): CustomerState {
  switch (action.type) {
    // * ACCOUNT INFO
    case CustomerActionType.START_ACCOUNT_INFO:
      return {
        ...state,
        isLoadingAccountInfo: true,
      };
    case CustomerActionType.FINISH_ACCOUNT_INFO:
      return {
        ...state,
        isLoadingAccountInfo: false,
      };

    case CustomerActionType.START_SIGN_IN:
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case CustomerActionType.SUCCESS_SIGN_IN:
      return {
        ...state,
        customer: action.customer,
        isLoggedIn: true,
        isLoading: false,
        error: null,
      };
    case CustomerActionType.FAILURE_SIGN_IN:
      return {
        ...state,
        customer: null,
        isLoggedIn: false,
        isLoading: false,
        error: action.error,
      };
    case CustomerActionType.START_SIGN_UP:
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case CustomerActionType.SUCCESS_SIGN_UP:
      return {
        ...state,
        isLoading: false,
        error: null,
      };
    case CustomerActionType.FAILURE_SIGN_UP:
      return {
        ...state,
        isLoading: false,
        error: action.error,
      };

    // Reset password
    case CustomerActionType.START_RESET_PASSWORD:
      return {
        ...state,
        isResetPasswordLoading: true,
        resetPasswordError: null,
      };
    case CustomerActionType.SUCCESS_RESET_PASSWORD:
      return {
        ...state,
        isResetPasswordLoading: false,
        resetPasswordError: null,
      };
    case CustomerActionType.FAILURE_RESET_PASSWORD:
      return {
        ...state,
        isResetPasswordLoading: false,
        resetPasswordError: action.error,
      };

    case CustomerActionType.LOGOUT:
      return {
        ...state,
        customer: null,
        isLoggedIn: false,
        isLoading: false,
        error: null,
      };
    case CustomerActionType.START_UPDATE_CUSTOMER:
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case CustomerActionType.SUCCESS_UPDATE_CUSTOMER:
      return {
        ...state,
        isLoading: false,
        error: null,
      };
    case CustomerActionType.FAILURE_UPDATE_CUSTOMER:
      return {
        ...state,
        isLoading: false,
        error: action.error,
      };

    // * ORDER HISTORIES
    case CustomerActionType.START_ORDER_HISTORIES:
      return {
        ...state,
        orderDetails: null,
        isOrderHistoriesLoading: true,
        orderHistoriesError: null,
      };
    case CustomerActionType.SUCCESS_ORDER_HISTORIES:
      return {
        ...state,
        isOrderHistoriesLoading: false,
        orderHistories: action.orderHistories,
      };
    case CustomerActionType.FAILURE_ORDER_HISTORIES:
      return {
        ...state,
        isOrderHistoriesLoading: false,
        orderHistoriesError: action.error,
      };

    // * ORDER DETAILS
    case CustomerActionType.START_ORDER_DETAILS:
      return {
        ...state,
        isOrderDetailsLoading: action.isOrderDetailsLoading,
        orderDetailsError: null,
      };
    case CustomerActionType.SUCCESS_ORDER_DETAILS:
      return {
        ...state,
        isOrderDetailsLoading: action.isOrderDetailsLoading,
        orderDetails: action.orderDetails,
      };
    case CustomerActionType.FAILURE_ORDER_DETAILS:
      return {
        ...state,
        isOrderDetailsLoading: action.isOrderDetailsLoading,
        orderDetailsError: action.error,
      };

    // * ADDRESSES
    case CustomerActionType.START_ADDRESSES:
      return {
        ...state,
        addresses: null,
        isAddressesLoading: true,
        addressesError: null,
      };
    case CustomerActionType.SUCCESS_ADDRESSES:
      return {
        ...state,
        isAddressesLoading: false,
        addresses: Object.entries(action.addresses).map(([key, value]) => ({
          ...value,
          id: key,
        })),
      };
    case CustomerActionType.FAILURE_ADDRESSES:
      return {
        ...state,
        isAddressesLoading: false,
        addressesError: action.error,
      };

    // * ADDRESS DETAILS
    case CustomerActionType.START_ADDRESS_DETAILS:
      return {
        ...state,
        addressDetails: null,
        isAddressDetailsLoading: true,
        addressDetailsError: null,
      };
    case CustomerActionType.SUCCESS_ADDRESS_DETAILS:
      return {
        ...state,
        isAddressDetailsLoading: false,
        addressDetails: action.address,
      };
    case CustomerActionType.FAILURE_ADDRESS_DETAILS:
      return {
        ...state,
        isAddressDetailsLoading: false,
        addressDetailsError: action.error,
      };

    // * CREATE ADDRESS
    case CustomerActionType.START_CREATE_ADDRESS:
      return {
        ...state,
        isCreateAddressLoading: true,
        createAddressError: null,
      };
    case CustomerActionType.SUCCESS_CREATE_ADDRESS:
      return {
        ...state,
        isCreateAddressLoading: false,
        createAddressError: null,
        addresses: state.addresses
          ? [...state.addresses, action.address]
          : [action.address],
      };
    case CustomerActionType.FAILURE_CREATE_ADDRESS:
      return {
        ...state,
        isCreateAddressLoading: false,
        createAddressError: action.error,
      };

    // * UPDATE ADDRESS
    case CustomerActionType.START_UPDATE_ADDRESS:
      return {
        ...state,
        isUpdateAddressLoading: true,
        updateAddressError: null,
      };
    case CustomerActionType.SUCCESS_UPDATE_ADDRESS:
      return {
        ...state,
        isUpdateAddressLoading: false,
        updateAddressError: null,
        addresses: state.addresses
          ? state.addresses.map((address) =>
              Number(address.id) === Number(action.address.id)
                ? action.address
                : address,
            )
          : [action.address],
      };
    case CustomerActionType.FAILURE_UPDATE_ADDRESS:
      return {
        ...state,
        isUpdateAddressLoading: false,
        updateAddressError: action.error,
      };

    // * DELETE ADDRESS
    case CustomerActionType.START_DELETE_ADDRESS:
      return {
        ...state,
        isDeleteAddressLoading: true,
        deleteAddressError: null,
      };
    case CustomerActionType.SUCCESS_DELETE_ADDRESS:
      return {
        ...state,
        isDeleteAddressLoading: false,
        deleteAddressError: null,
        addresses: state.addresses
          ? state.addresses.filter((address) => address.id !== action.idAddress)
          : null,
      };
    case CustomerActionType.FAILURE_DELETE_ADDRESS:
      return {
        ...state,
        isDeleteAddressLoading: false,
        deleteAddressError: action.error,
      };

    default:
      throw new Error("Unhandled action");
  }
}

export function CustomerProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(customerReducer, initialCustomerState);

  return (
    <CustomerDispatchContext.Provider value={dispatch}>
      <CustomerStateContext.Provider value={useMemo(() => state, [state])}>
        {children}
      </CustomerStateContext.Provider>
    </CustomerDispatchContext.Provider>
  );
}

export function useCustomerState() {
  const state = useContext(CustomerStateContext);
  if (!state) throw new Error("CustomerStateContext not found");
  return state;
}

export function useCustomerDispatch() {
  const dispatch = useContext(CustomerDispatchContext);
  if (!dispatch) throw new Error("CustomerDispatchContext not found");
  return dispatch;
}

const signInCustomer = async (
  signInRequestProps: SignInRequestProps,
  dispatch: CustomerDispatch,
) => {
  dispatch({ type: CustomerActionType.START_SIGN_IN });

  try {
    const response = await axiosInstance.post(
      `/rest${LoginAPI}`,
      signInRequestProps,
    );

    if (response.data.code === 306) {
      dispatch({
        type: CustomerActionType.FAILURE_SIGN_IN,
        error: "Nieprawidłowy adres email lub hasło",
      });
      return {
        status: response.data.code,
        success: false,
        error: response.data.error,
      };
    }

    if (response.data.code === 200) {
      const customer: Customer = snakeToCamelCaseObject(
        response.data.psdata.user,
      );

      setTimeout(() => {
        dispatch({
          type: CustomerActionType.SUCCESS_SIGN_IN,
          customer,
        });
        return { status: response.data.code, success: true, data: customer };
      }, 1000);
    }

    return {
      status: response.data.code,
      success: false,
      error: response.data.error,
    };
  } catch (error: any) {
    const errorMessage = error.response?.data?.error || "Sign in failed";
    dispatch({ type: CustomerActionType.FAILURE_SIGN_IN, error: errorMessage });
    return {
      status: error.response?.status,
      success: false,
      error: errorMessage,
    };
  }
};

const signUpCustomer = async (
  signUpRequestProps: SignUpRequestProps,
  dispatch: CustomerDispatch,
) => {
  dispatch({ type: CustomerActionType.START_SIGN_UP });

  try {
    const response = await axiosInstance.post(
      `/rest${RegisterAPI}`,
      signUpRequestProps,
    );

    if (response.data.code === 308) {
      return {
        status: response.data.code,
        success: false,
        error: response.data.error,
      };
    }

    if (response.data.code === 200) {
      dispatch({ type: CustomerActionType.SUCCESS_SIGN_UP });
      return { status: response.data.code, success: true, data: response.data };
    }

    return {
      status: response.data.code,
      success: false,
      error: response.data.error,
    };
  } catch (error: any) {
    const errorMessage = error.response?.data?.error || "Sign up failed";
    dispatch({ type: CustomerActionType.FAILURE_SIGN_UP, error: errorMessage });
    return { success: false, error: errorMessage };
  }
};

const updateCustomer = async (
  updateCustomerRequestProps: AccountFormValues,
  dispatch: CustomerDispatch,
) => {
  dispatch({ type: CustomerActionType.START_UPDATE_CUSTOMER });

  try {
    const response = await axiosInstance.post(`/rest${UpdateCustomerAPI}`, {
      ...updateCustomerRequestProps,
      gender: updateCustomerRequestProps.idGender,
      new_password: updateCustomerRequestProps.newPassword,
    });

    if (response.data.code === 200) {
      dispatch({ type: CustomerActionType.SUCCESS_UPDATE_CUSTOMER });
    }

    return {
      status: response.data.code,
      success: false,
      error: response.data.error,
    };
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.error || "Update customer failed";
    dispatch({
      type: CustomerActionType.FAILURE_UPDATE_CUSTOMER,
      error: errorMessage,
    });
    return { success: false, error: errorMessage };
  }
};

const logoutCustomer = async (dispatch: CustomerDispatch) => {
  await axiosInstance.get(`/rest${LogoutAPI}`).then(() => {
    dispatch({ type: CustomerActionType.LOGOUT });
  });
};

const getAccountInfo = async (dispatch: CustomerDispatch) => {
  dispatch({ type: CustomerActionType.START_ACCOUNT_INFO });

  const response = await axiosInstance.get(`/rest${AccountInfoAPI}`);
  if (response.data.psdata) {
    const customer: Customer = snakeToCamelCaseObject(response.data.psdata);
    dispatch({ type: CustomerActionType.SUCCESS_SIGN_IN, customer });
  }

  setTimeout(() => {
    dispatch({ type: CustomerActionType.FINISH_ACCOUNT_INFO });
  }, 1000);
};

const getOrderHistories = async (dispatch: CustomerDispatch) => {
  dispatch({ type: CustomerActionType.START_ORDER_HISTORIES });

  const response = await axiosInstance.get(`/rest${OrderHistoryAPI}`);
  if (response.data.psdata) {
    const orderHistories: OrderHistorySimple[] = snakeToCamelCaseObject(
      response.data.psdata,
    );

    setTimeout(() => {
      dispatch({
        type: CustomerActionType.SUCCESS_ORDER_HISTORIES,
        orderHistories,
      });
    }, 1000);
  } else {
    dispatch({
      type: CustomerActionType.FAILURE_ORDER_HISTORIES,
      error: response.data.error,
    });
  }
};

const getOrderDetails = async (
  idOrder: string,
  orderDetails: {
    [key: number | string]: Order | null;
  } | null,
  isOrderDetailsLoading: {
    [key: number | string]: boolean;
  },
  dispatch: CustomerDispatch,
) => {
  const updatedLoadingState = {
    ...isOrderDetailsLoading,
    [idOrder]: true,
  };

  dispatch({
    type: CustomerActionType.START_ORDER_DETAILS,
    isOrderDetailsLoading: updatedLoadingState,
  });

  const response = await axiosInstance.get(
    `/rest${OrderHistoryAPI}?id_order=${idOrder}`,
  );
  if (response.data.psdata) {
    const fetchedOrderDetails: Order = snakeToCamelCaseObject(
      response.data.psdata,
    );

    const newOrderDetails = {
      ...(orderDetails || {}),
      [idOrder]: fetchedOrderDetails,
    };

    const finalLoadingState = {
      ...updatedLoadingState,
      [idOrder]: false,
    };

    dispatch({
      type: CustomerActionType.SUCCESS_ORDER_DETAILS,
      isOrderDetailsLoading: finalLoadingState,
      orderDetails: newOrderDetails,
    });
  } else {
    const finalLoadingState = {
      ...updatedLoadingState,
      [idOrder]: false,
    };

    dispatch({
      type: CustomerActionType.FAILURE_ORDER_DETAILS,
      isOrderDetailsLoading: finalLoadingState,
      error: response.data.error,
    });
  }
};

const getAddresses = async (dispatch: CustomerDispatch) => {
  dispatch({ type: CustomerActionType.START_ADDRESSES });

  const response = await axiosInstance.get(`/rest${AllAddressesAPI}`);
  if (response.data.psdata) {
    const addresses: Address[] = snakeToCamelCaseObject(response.data.psdata);
    setTimeout(() => {
      dispatch({ type: CustomerActionType.SUCCESS_ADDRESSES, addresses });
    }, 1000);
  } else {
    dispatch({
      type: CustomerActionType.FAILURE_ADDRESSES,
      error: response.data.error,
    });
  }
};

const getAddressDetails = async (
  idAddress: string,
  dispatch: CustomerDispatch,
) => {
  dispatch({ type: CustomerActionType.START_ADDRESS_DETAILS });

  const response = await axiosInstance.get(
    `/rest${AddressDetailsAPI}?id_address=${idAddress}`,
  );
  if (response.data.psdata) {
    const address: Address = snakeToCamelCaseObject(response.data.psdata);
    dispatch({ type: CustomerActionType.SUCCESS_ADDRESS_DETAILS, address });
  } else {
    dispatch({
      type: CustomerActionType.FAILURE_ADDRESS_DETAILS,
      error: response.data.error,
    });
  }
};

const createAddress = async (
  createAddressRequestProps: AddressFormValues,
  dispatch: CustomerDispatch,
) => {
  dispatch({ type: CustomerActionType.START_CREATE_ADDRESS });

  try {
    const response = await axiosInstance.post(
      `/rest${AddressDetailsAPI}`,
      camelToSnakeCaseObject(createAddressRequestProps),
    );

    if (response.data.psdata) {
      const address: Address = snakeToCamelCaseObject(response.data.psdata);
      dispatch({ type: CustomerActionType.SUCCESS_CREATE_ADDRESS, address });
      return {
        status: response.data.code,
        success: true,
        data: address,
      };
    } else {
      dispatch({
        type: CustomerActionType.FAILURE_CREATE_ADDRESS,
        error: response.data.error,
      });
      return {
        status: response.data.code,
        success: false,
        error: response.data.error,
      };
    }
  } catch (error: any) {
    const errorMessage = error.response?.data?.error || "Create address failed";
    dispatch({
      type: CustomerActionType.FAILURE_CREATE_ADDRESS,
      error: errorMessage,
    });
    return { success: false, error: errorMessage };
  }
};

const updateAddress = async (
  idAddress: string,
  updateAddressRequestProps: AddressFormValues,
  dispatch: CustomerDispatch,
) => {
  dispatch({ type: CustomerActionType.START_UPDATE_ADDRESS });

  try {
    const response = await axiosInstance.post(
      `/rest${AddressDetailsAPI}`,
      camelToSnakeCaseObject({
        ...updateAddressRequestProps,
        idAddress,
      }),
    );

    if (response.data.psdata) {
      const address: Address = snakeToCamelCaseObject(response.data.psdata);
      dispatch({ type: CustomerActionType.SUCCESS_UPDATE_ADDRESS, address });
      return {
        status: response.data.code,
        success: true,
        data: address,
      };
    } else {
      dispatch({
        type: CustomerActionType.FAILURE_UPDATE_ADDRESS,
        error: response.data.error,
      });
      return {
        status: response.data.code,
        success: false,
        error: response.data.error,
      };
    }
  } catch (error: any) {
    const errorMessage = error.response?.data?.error || "Update address failed";
    dispatch({
      type: CustomerActionType.FAILURE_UPDATE_ADDRESS,
      error: errorMessage,
    });
    return { success: false, error: errorMessage };
  }
};

const deleteAddress = async (idAddress: string, dispatch: CustomerDispatch) => {
  dispatch({ type: CustomerActionType.START_DELETE_ADDRESS });

  const response = await axiosInstance.delete(`/rest${AddressDetailsAPI}`, {
    data: {
      id_address: idAddress,
    },
  });

  if (response.data.psdata) {
    dispatch({
      type: CustomerActionType.SUCCESS_DELETE_ADDRESS,
      idAddress,
    });
    return {
      status: response.data.code,
      success: true,
      data: response.data.psdata,
    };
  } else {
    dispatch({
      type: CustomerActionType.FAILURE_DELETE_ADDRESS,
      error: response.data.error,
    });
    return {
      status: response.data.code,
      success: false,
      error: response.data.error,
    };
  }
};

const resetPassword = async (email: string, dispatch: CustomerDispatch) => {
  dispatch({ type: CustomerActionType.START_RESET_PASSWORD });

  try {
    const response = await axiosInstance.post(`/rest${ResetPasswordAPI}`, {
      email,
    });

    if (response.data.success) {
      dispatch({ type: CustomerActionType.SUCCESS_RESET_PASSWORD });
      return {
        status: response.data.code,
        success: true,
        data: response.data,
      };
    } else {
      dispatch({
        type: CustomerActionType.FAILURE_RESET_PASSWORD,
        error: response.data.error,
      });
      return {
        status: response.data.code,
        success: false,
        error: response.data.error,
      };
    }
  } catch (error: any) {
    const errorMessage = error.response?.data?.error || "Reset password failed";
    dispatch({
      type: CustomerActionType.FAILURE_RESET_PASSWORD,
      error: errorMessage,
    });
    return { success: false, error: errorMessage };
  }
};

export function useCustomer() {
  const state = useCustomerState();
  const dispatch = useCustomerDispatch();

  const signInFunc = async (signInRequestProps: SignInRequestProps) =>
    signInCustomer(signInRequestProps, dispatch);

  const signUpFunc = async (signUpRequestProps: SignUpRequestProps) =>
    signUpCustomer(signUpRequestProps, dispatch);

  const logoutFunc = async () => logoutCustomer(dispatch);

  const getAccountInfoFunc = async () => getAccountInfo(dispatch);

  const updateCustomerFunc = async (
    updateCustomerRequestProps: AccountFormValues,
  ) => updateCustomer(updateCustomerRequestProps, dispatch);

  const getOrderHistoriesFunc = async () => getOrderHistories(dispatch);

  const getOrderDetailsFunc = async (idOrder: string) => {
    if (state.isOrderDetailsLoading[idOrder] || state.orderDetails?.[idOrder]) {
      return;
    }

    getOrderDetails(
      idOrder,
      state.orderDetails,
      state.isOrderDetailsLoading,
      dispatch,
    );
  };

  const getAddressesFunc = async () => getAddresses(dispatch);

  const getAddressDetailsFunc = async (idAddress: string) =>
    getAddressDetails(idAddress, dispatch);

  const createAddressFunc = async (
    createAddressRequestProps: AddressFormValues,
  ) => createAddress(createAddressRequestProps, dispatch);

  const updateAddressFunc = async (
    idAddress: string,
    updateAddressRequestProps: AddressFormValues,
  ) => updateAddress(idAddress, updateAddressRequestProps, dispatch);

  const deleteAddressFunc = async (idAddress: string) =>
    deleteAddress(idAddress, dispatch);

  const resetAddresses = async () => {
    dispatch({ type: CustomerActionType.SUCCESS_ADDRESSES, addresses: [] });
  };

  const resetOrderHistories = async () => {
    dispatch({
      type: CustomerActionType.SUCCESS_ORDER_HISTORIES,
      orderHistories: [],
    });
  };

  const resetPasswordFunc = async (email: string) =>
    resetPassword(email, dispatch);

  return {
    state,
    signIn: signInFunc,
    signUp: signUpFunc,
    logout: logoutFunc,
    getAccountInfo: getAccountInfoFunc,
    updateCustomer: updateCustomerFunc,
    getOrderHistories: getOrderHistoriesFunc,
    getOrderDetails: getOrderDetailsFunc,
    getAddresses: getAddressesFunc,
    getAddressDetails: getAddressDetailsFunc,
    createAddress: createAddressFunc,
    updateAddress: updateAddressFunc,
    deleteAddress: deleteAddressFunc,
    resetAddresses,
    resetOrderHistories,
    resetPassword: resetPasswordFunc,
  };
}
